import React from 'react';
import { Navigate } from 'react-router-dom';
import { NavigateWithParams } from '@spacefill/shared/src/utils/RouterUtils';

const LogisticManagementMasterItemsListNew = async () => import('../../pages/logistic-management/inventory/items/List').then((module) => ({ Component: module.LogisticManagementMasterItemsList }));
const LogisticManagementMasterItemsViewNew = async () => import('../../pages/logistic-management/inventory/items/View').then((module) => ({ Component: module.LogisticManagementMasterItemsView }));
const LogisticManagementMasterItemsNew = async () => import('../../pages/logistic-management/inventory/items/New').then((module) => ({ Component: module.LogisticManagementMasterItemsNew }));

const LogisticManagementOrdersList = async () => import('../../pages/logistic-management/orders/List').then((module) => ({ Component: module.LogisticManagementOrdersList }));

const LogisticManagementStockSituation = async () => import('../../pages/logistic-management/inventory/stock-situation').then((module) => ({ Component: module.LogisticManagementStockSituation }));
const LogisticManagementStockSituationByWarehouse = async () => import('../../pages/logistic-management/warehouses/inventory/stock-situation').then((module) => ({ Component: module.LogisticManagementStockSituationByWarehouse }));
const LogisticManagementStockAdjustmentByWarehouse = async () => import('../../pages/logistic-management/warehouses/inventory/stock-situation/new-adjustment/StockAdjustment').then((module) => ({ Component: module.LogisticManagementStockAdjustmentByWarehouse }));
const LogisticManagementWarehouseConfigurationsView = async () => import('../../pages/logistic-management/warehouses/customer-warehouse-configurations/ViewAsWarehouseUser').then((module) => ({ Component: module.WarehouseConfigurationsView }));
const LogisticManagementAccurateStockSituation = async () => import('../../pages/logistic-management/inventory/accurate-stock-situation').then((module) => ({ Component: module.LogisticManagementAccurateStockSituation }));

const LogisticManagementOrdersRedirectView = async () => import('../../pages/logistic-management/orders/RedirectView').then((module) => ({ Component: module.LogisticManagementOrdersRedirectView }));

const LogisticManagementWarehousesOrdersList = async () => import('../../pages/logistic-management/warehouses/orders/List').then((module) => ({ Component: module.LogisticManagementWarehousesOrdersList }));
const LogisticManagementWarehousesOrdersView = async () => import('../../pages/logistic-management/warehouses/orders/view/View').then((module) => ({ Component: module.LogisticManagementWarehousesOrdersView }));
const LogisticManagementWarehousesNewOrdersEntry = async () => import('../../pages/logistic-management/warehouses/orders/new/Entry').then((module) => ({ Component: module.LogisticManagementWarehousesNewOrdersEntry }));
const LogisticManagementWarehousesNewOrdersExit = async () => import('../../pages/logistic-management/warehouses/orders/new/Exit').then((module) => ({ Component: module.LogisticManagementWarehousesNewOrdersExit }));
const LogisticManagementWarehousesOrdersAdjustments = async () => import('../../pages/logistic-management/warehouses/orders/Adjustments').then((module) => ({ Component: module.LogisticManagementWarehousesOrdersAdjustments }));
const LogisticManagementWarehousesEditOrdersRedirectEntryOrExit = async () => import('../../pages/logistic-management/warehouses/orders/edit/index').then((module) => ({ Component: module.LogisticManagementWarehousesEditOrdersRedirectEntryOrExit }));
const LogisticManagementWarehousesEditOrders = async () => import('../../pages/logistic-management/warehouses/orders/edit/Edit').then((module) => ({ Component: module.LogisticManagementWarehousesEditOrders }));

const LogisticManagementWarehousesTodoTasksList = async () => import('../../pages/logistic-management/warehouses/todo-tasks/List').then((module) => ({ Component: module.LogisticManagementWarehousesTodoTasksList }));

const LogisticProviderDataView = async () => import('../../pages/logistic-management/analytics/View/View').then((module) => ({ Component: module.DataView }));
const DocumentsPage = async () => import('../../pages/DocumentsPage').then((module) => ({ Component: module.DocumentsPage }));

const LogisticManagementBillingList = async () => import('../../pages/logistic-management/warehouses/billing/List').then((module) => ({ Component: module.LogisticManagementBillingList }));
const LogisticManagementBillingView = async () => import('../../pages/logistic-management/warehouses/billing/View').then((module) => ({ Component: module.LogisticManagementBillingView }));
const LogisticManagementBillingEdit = async () => import('../../pages/logistic-management/warehouses/billing/Edit').then((module) => ({ Component: module.LogisticManagementBillingEdit }));

const getLogisticProviderLogisticManagementRoutes = (user, _features) => {
    if (user.role !== 'PROVIDER' || (user?.customerWarehouseConfigurationsWithLogisticManagement ?? []).length === 0) {
        return [];
    }

    return [
        // Global routes

        //  Data View
        {
            path: '/logistic-management/analytics/',
            lazy: LogisticProviderDataView,
            condition: user.features.hasAnalyticsEnabled
        },
        //  Documents
        {
            path: '/documents/',
            lazy: DocumentsPage
        },
        // Orders
        {
            path: '/logistic-management/orders/',
            lazy: LogisticManagementOrdersList
        },
        {
            path: '/logistic-management/orders/:orderId/',
            lazy: LogisticManagementOrdersRedirectView
        },

        // Master items
        {
            path: '/logistic-management/inventory/items/',
            lazy: LogisticManagementMasterItemsListNew
        },
        {
            path: '/logistic-management/inventory/items/new',
            lazy: LogisticManagementMasterItemsNew
        },
        {
            path: '/logistic-management/inventory/items/:masterItemId/',
            lazy: LogisticManagementMasterItemsViewNew
        },

        // Stock situation
        {
            path: '/logistic-management/inventory/stock-situation/',
            lazy: LogisticManagementStockSituation
        },
        {
            path: '/logistic-management/inventory/stock-situation/references/',
            lazy: user.toggleFeatures.includes('accurateStock') ? LogisticManagementAccurateStockSituation : LogisticManagementStockSituation
        },
        {
            path: '/logistic-management/inventory/stock-situation/references/forecasted/',
            lazy: user.toggleFeatures.includes('accurateStock') ? LogisticManagementAccurateStockSituation : LogisticManagementStockSituation
        },
        {
            path: '/logistic-management/inventory/stock-situation/references/accurate/',
            lazy: LogisticManagementAccurateStockSituation
        },
        {
            path: '/logistic-management/inventory/stock-situation/unique-identifier/',
            lazy: LogisticManagementStockSituation
        },
        {
            path: '/logistic-management/inventory/stock-situation/references/accurate/forecasted/',
            lazy: LogisticManagementAccurateStockSituation
        },

        // By warehouse

        // To-do tasks
        {
            path: '/logistic-management/warehouses/:warehouseId/todo-tasks/',
            lazy: LogisticManagementWarehousesTodoTasksList
        },

        // Orders
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/',
            lazy: LogisticManagementWarehousesOrdersList
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/new-entry',
            lazy: LogisticManagementWarehousesNewOrdersEntry
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/new-exit',
            lazy: LogisticManagementWarehousesNewOrdersExit
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/',
            lazy: LogisticManagementWarehousesOrdersView
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/order-adjustment/',
            lazy: LogisticManagementWarehousesOrdersAdjustments
        },

        // Edit order
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/edit',
            lazy: LogisticManagementWarehousesEditOrdersRedirectEntryOrExit,
            condition: _features?.enable_uikit_order_edit_feature
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/edit-entry',
            lazy: LogisticManagementWarehousesEditOrders
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/edit-exit',
            lazy: LogisticManagementWarehousesEditOrders
        },

        // Warehouses : redirect to stock situation
        {
            path: '/logistic-management/warehouses/:warehouseId',
            element: <NavigateWithParams to={(params) => `/logistic-management/warehouses/${params.warehouseId}/inventory/stock-situation/`} />

        },

        // Stock situation
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/',
            lazy: LogisticManagementStockSituationByWarehouse
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/references/',
            lazy: user.toggleFeatures.includes('accurateStock') ? LogisticManagementAccurateStockSituation : LogisticManagementStockSituationByWarehouse
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/references/forecasted',
            lazy: user.toggleFeatures.includes('accurateStock') ? LogisticManagementAccurateStockSituation : LogisticManagementStockSituationByWarehouse
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/references/accurate/',
            lazy: LogisticManagementAccurateStockSituation
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/references/accurate-forecasted',
            lazy: LogisticManagementAccurateStockSituation
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/unique-identifier/',
            lazy: LogisticManagementStockSituationByWarehouse
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/new-adjustment/',
            lazy: LogisticManagementStockAdjustmentByWarehouse
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/warehouse-configurations/',
            lazy: LogisticManagementWarehouseConfigurationsView
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/warehouse-configurations/:configurationId/',
            lazy: LogisticManagementWarehouseConfigurationsView
        },

        // Billing invoices
        {
            path: '/logistic-management/warehouses/:warehouseId/billing/',
            lazy: LogisticManagementBillingList
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/billing/:customerWarehouseConfigurationId/',
            lazy: LogisticManagementBillingList
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/billing/:customerWarehouseConfigurationId/invoices/',
            element: <NavigateWithParams to={(params) => `/logistic-management/warehouses/${params.warehouseId}/billing/${params.customerWarehouseConfigurationId}/`} />
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/billing/:customerWarehouseConfigurationId/invoices/:invoiceId/',
            lazy: LogisticManagementBillingView
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/billing/:customerWarehouseConfigurationId/invoices/:invoiceId/edit',
            lazy: LogisticManagementBillingEdit
        },

        // Default
        {
            path: '/logistic-management/*',
            element: <Navigate to='/' />
        }
    ];
};

export { getLogisticProviderLogisticManagementRoutes };
